import { useEffect, useState, useMemo } from "react"
import { Loader } from "lucide-react"
import { useParams, useLocation, useNavigate } from "react-router"
import { useTranslation } from "react-i18next"

import { Tabs, TabsList, TabsTrigger } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import {
  useGetWorkspaceUsersApi,
  workspaceUsersResponseToMembers,
  useAddWorkspaceMembersApi,
  useGetWorkspaceApi,
  workspaceResponseToWorkspace,
} from "@/hooks/api"
import { ErrorPage } from "@/components/System"
import { hasPermission } from "@/lib/utils"
import { Permission, Workspace } from "@/types"
import { useStore } from "@/store"

import { HistoryList } from "./Histories"
import {
  ChannelsTab,
  ApiKeysTab,
  FormRequestsTab,
  WorkspacePaymentsTab,
  WorkspaceMembersDialog,
  UserManagementTab,
  NotificationsTab,
} from "./components"
import AgentTab from "./components/AgentTab"

const WORKSPACE_DETAIL_TABS = [
  "edit-agents",
  "conversations",
  "histories",
  "form-submissions",
  "api-keys",
  "payments",
  "channels",
  "user-management",
  "notifications",
] as const

type WorkspaceDetailTab = (typeof WORKSPACE_DETAIL_TABS)[number]

const DEFAULT_WORKSPACE_DETAIL_TAB: WorkspaceDetailTab = "edit-agents"
const OPERATION_TABS: WorkspaceDetailTab[] = ["form-submissions", "payments"]

const isWorkspaceDetailTab = (
  tab: string | null
): tab is WorkspaceDetailTab => {
  if (!tab) return false
  return (WORKSPACE_DETAIL_TABS as readonly string[]).includes(tab)
}

const WorkspaceDetailPage = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [currentOrganization, setWorkspaces] = useStore((state) => [
    state.currentOrganization,
    state.setWorkspaces,
  ])
  const { workspaceId = "" } = useParams()

  const [workspace, setWorkspace] = useState<Workspace>()
  const [showMembersDialog, setShowMembersDialog] = useState<boolean>(false)
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false)

  // APIs
  const {
    error: workspaceError,
    data: workspaceResp,
    fetchFn: getWorkspace,
  } = useGetWorkspaceApi(currentOrganization!.id, workspaceId)

  const {
    error: membersError,
    data: membersResp,
    fetchFn: getMembers,
  } = useGetWorkspaceUsersApi(currentOrganization!.id, workspaceId)

  const { fetchFn: addMembers } = useAddWorkspaceMembersApi(
    currentOrganization!.id,
    workspaceId
  )

  const rootPath = location.pathname.split("/").slice(0, 3).join("/")

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const targetTab = useMemo<WorkspaceDetailTab>(() => {
    const tab = searchParams.get("tab")
    if (tab === "histories") return "conversations"
    if (isWorkspaceDetailTab(tab)) return tab
    return DEFAULT_WORKSPACE_DETAIL_TAB
  }, [searchParams])

  useEffect(() => {
    if (searchParams.get("tab") !== "histories") return

    const nextParams = new URLSearchParams(searchParams.toString())
    nextParams.set("tab", "conversations")
    navigate(`${rootPath}?${nextParams.toString()}`, {
      replace: true,
    })
  }, [navigate, rootPath, searchParams])

  // Auto-redirect to default tab on initial page load only
  // This ensures a clean URL structure without interfering with:
  // - Bookmarked URLs with specific tabs (e.g., ?tab=histories)
  // - Browser back/forward navigation
  // - Direct URL access with tab parameters
  // Empty deps array ensures this runs once on mount, not on every URL change
  useEffect(() => {
    const tab = searchParams.get("tab")
    if (!isWorkspaceDetailTab(tab)) {
      navigate(`${rootPath}?tab=${DEFAULT_WORKSPACE_DETAIL_TAB}`, {
        replace: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setWorkspace(undefined)
    getWorkspace()
  }, [getWorkspace, workspaceId])

  useEffect(() => {
    if (showMembersDialog) getMembers()
  }, [showMembersDialog, getMembers])

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    if (query.get("open") === "members") setShowMembersDialog(true)
  }, [location.search])

  useEffect(() => {
    if (!workspaceResp?.data) return

    const newWorkspace = workspaceResponseToWorkspace(workspaceResp.data)
    if (newWorkspace.id !== workspaceId) return

    setWorkspace(newWorkspace)
    setWorkspaces((prev) => [
      ...prev.filter((w) => w.id !== newWorkspace.id),
      newWorkspace,
    ])
  }, [workspaceId, workspaceResp, setWorkspaces])

  const handleWorkspaceUpdated = (nextWorkspace: Workspace) => {
    setWorkspace(nextWorkspace)
    setWorkspaces((prev) => [
      ...prev.filter((w) => w.id !== nextWorkspace.id),
      nextWorkspace,
    ])
  }

  const handleTabChange = (newTab: string) => {
    navigate(`${rootPath}?tab=${newTab}`, { flushSync: true })
  }

  const workspaceMembers = useMemo(
    () =>
      membersResp?.data ? workspaceUsersResponseToMembers(membersResp) : [],
    [membersResp]
  )

  if (workspaceError) {
    return (
      <ErrorPage
        redirectReload={false}
        message={t("workspace.detail.errors.loadFailed")}
      />
    )
  }

  if (!workspace || workspace.id !== workspaceId) return null

  const canManagePaymentRequests = hasPermission(
    workspace.myPermissions,
    Permission.WsUpdateResources
  )
  const isPaymentRequestDetailView =
    canManagePaymentRequests &&
    targetTab === "payments" &&
    Boolean(searchParams.get("paymentRequestId"))
  const showOperationsSubnav =
    !isPaymentRequestDetailView && OPERATION_TABS.includes(targetTab)
  const isHistoriesTab = targetTab === "conversations"
  const subnavTriggerClassName =
    "flex-none h-auto bg-transparent rounded-none px-0 py-1 text-sm font-medium " +
    "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50 " +
    "shadow-none focus-visible:ring-0 focus-visible:outline-none " +
    "data-[state=active]:bg-transparent dark:data-[state=active]:bg-transparent data-[state=active]:shadow-none " +
    "data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-50 " +
    "border-x-0 border-t-0 border-b-2 border-b-transparent " +
    "data-[state=active]:border-b-slate-900 dark:data-[state=active]:border-b-slate-50 " +
    "focus-visible:border-b-slate-400 dark:focus-visible:border-b-slate-500"
  return (
    <div
      className={cn(
        "w-full",
        isHistoriesTab ? "flex h-full min-h-0 flex-col" : "mb-10"
      )}
    >
      {!workspace ? (
        <Loader className="inline mr-2 mb-1 h-4 w-4 animate-spin" />
      ) : (
        <>
          {!isPaymentRequestDetailView ? (
            <div className="mb-2 space-y-3 md:mb-4">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                  {workspace.name}
                </h1>
              </div>

              {showOperationsSubnav ? (
                <Tabs value={targetTab} onValueChange={handleTabChange}>
                  <TabsList className="flex justify-start gap-6 bg-transparent dark:bg-transparent p-0 rounded-none border-0 h-auto">
                    <TabsTrigger
                      value="form-submissions"
                      className={subnavTriggerClassName}
                    >
                      {t("workspace.tabs.formSubmissions")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="payments"
                      className={subnavTriggerClassName}
                    >
                      {t("workspace.tabs.payments")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : null}
            </div>
          ) : null}

          <div
            className={cn(
              "mt-4 md:mt-6",
              isHistoriesTab && "min-h-0 flex-1 overflow-hidden"
            )}
          >
            {targetTab === "edit-agents" && <AgentTab workspace={workspace} />}
            {targetTab === "conversations" && (
              <HistoryList workspace={workspace} />
            )}
            {targetTab === "form-submissions" && (
              <FormRequestsTab workspace={workspace} />
            )}
            {targetTab === "api-keys" && <ApiKeysTab workspace={workspace} />}
            {targetTab === "payments" && (
              <WorkspacePaymentsTab workspace={workspace} />
            )}
            {targetTab === "channels" && <ChannelsTab workspace={workspace} />}
            {targetTab === "notifications" && (
              <NotificationsTab workspace={workspace} />
            )}
            {targetTab === "user-management" && (
              <UserManagementTab
                workspace={workspace}
                onWorkspaceUpdated={handleWorkspaceUpdated}
              />
            )}
          </div>
        </>
      )}

      <WorkspaceMembersDialog
        organization={currentOrganization!}
        workspace={workspace}
        members={workspaceMembers}
        membersLoaded={Boolean(membersResp)}
        membersError={membersError}
        open={showMembersDialog}
        addDialogOpen={showAddDialog}
        onOpenChange={setShowMembersDialog}
        onAddDialogOpenChange={setShowAddDialog}
        onConfirmAddMembers={(newUsers) => {
          addMembers({
            jsonBody: { user_ids: newUsers.map((u) => Number(u.id)) },
            callbackFn: () => {
              setShowAddDialog(false)
              getMembers()
            },
          })
        }}
      />
    </div>
  )
}

export default WorkspaceDetailPage
